<template>
  <span/>
</template>

<script>
import axios from 'axios';
import { mapGetters, mapMutations, mapActions } from 'vuex';
import { isSafari } from 'mobile-device-detect';
import { countryCodeForCurrentMarket } from '../detect-market';
import EventBus from '../resources/eventBus';
import countriesList from '@/assets/countries.json';

// {{ transl.App.welcome.interpolate({ name: user.name, lastName: user.last_name }) }}
// DO NOT use transl inside data() for defining strings
// use transl in mounted instead => see HomeInfo

// MazInputPhoneNumber's only-countries prop needs real ISO 3166-1 alpha-2
const MARKET_TO_ISO_COUNTRY = { uk: 'GB', 'ch-fr': 'CH', 'ch-de': 'CH', 'be-fr': 'BE', 'be-nl': 'BE' };

// Every market in countries.json is approved for this whitelist. Markets
// that share one ISO country (be/be-fr/be-nl -> BE, ch-fr/ch-de -> CH) are
// deduped to a single entry, keeping whichever market's title is seen first.
const WHITELISTED_COUNTRIES = [...countriesList
  .filter(({ value }) => value)
  .reduce((byIsoCode, { value, title }) => {
    const isoCode = MARKET_TO_ISO_COUNTRY[value] || value.toUpperCase();
    if (!byIsoCode.has(isoCode)) {
      byIsoCode.set(isoCode, { value: isoCode, title });
    }
    return byIsoCode;
  }, new Map())
  .values()];

export default {
  name: 'GlobalMixin',
  computed: {
    ...mapGetters(['transl', 'language',/* 'user', 'roles', 'permissions', 'tenants'*/]),
    wsReady() {
      return this.$socket.readyState === this.$socket.OPEN;
    },
    // get country code from URL query or detect from market
    countryCode() {
      const { countryCode } = this.$route.query;
      return countryCode || this.getCountryCode();
    },
    prefCountries() {
      return [this.getCountryCode()];
    },
    whitelistedCountries() {
      return WHITELISTED_COUNTRIES;
    },
    // MazInputPhoneNumber's only-countries prop wants plain ISO codes, not
    // { value, title } objects - use this for that binding instead.
    whitelistedCountryCodes() {
      return WHITELISTED_COUNTRIES.map(({ value }) => value);
    },
    /*deviceModel() {
      return navigator.userAgent;
    }*/
    isMarketPicker() {
      const name = this.$route.name;
      return name === 'MarketPicker';
    },
    isHomePage() {
      const name = this.$route.name;
      return name === 'Home' || name === 'UserStart';
    },
    isOnUserLanding() {
      const name = this.$route.name;
      return name === 'UserStart';
    },
    isOnOtherPage() {
      const path = this.$route.path;
      const name = this.$route.name;
      return path.includes('/info') || path.includes('/faq') || path.includes('/policy')
        || path.includes('/data') || path.includes('/email') || path.includes('/close')
        || name === 'UserPV';
    },
    isOnBrandPage() {
      const path = this.$route.path;
      return path.includes('/e/') || path.includes('/c/') || path.includes('/a/');
    },
    isOnUserPage() {
      const path = this.$route.path;
      return (path.includes('/u-p') || path.includes('/u-set') || path.includes('/u-devices') || path.includes('/u-email')
        // || path.includes('/u-p/shared') || path.includes('/u-p/activations') || path.includes('/u-p/history')
        // || path.includes('/u-p/receipts') || path.includes('/u-p/activations') || path.includes('/u-p/history')
      )
        && !path.includes('/u-p/club/');
    },
    isOnUserClubPage() {
      const path = this.$route.path;
      return path.includes('/u-p/club/');
    },
    isOnUserBrandPage() {
      const path = this.$route.path;
      return path.includes('/u-e/') || path.includes('/u-c/') || path.includes('/u-a/');
    },
    isOnPvPage() {
      const name = this.$route.name;
      return name === 'UserPV';
    },
    isOnAnyBrandPage() {
      return this.isOnBrandPage || this.isOnUserBrandPage || this.isOnPvPage;
    },
    isOnPartnerPage() {
      const path = this.$route.path;
      return path.includes('/partner') || path.includes('/c-') || path.includes('/new-company');
    },
    isOnAdminPage() {
      const path = this.$route.path;
      return path.includes('/admin');
    },
    isOnGamePage() {
      const path = this.$route.path;
      return path.includes('/g/');
    },
    isTesting() {
      return this.windowOrigin.includes('test.enginio.io')
        || this.windowOrigin.includes('enginio.io/test')  
        || this.windowOrigin.includes('localhost');
    },
    isDeveloping() {
      return this.windowOrigin.includes('localhost');
    },
    theMarket() {
      return this.$route?.params?.market;
    },
    // Default null — overridden by EntityMixin's computed on components that use it
    localCompany() {
      return null;
    },
    faqUrl() {
      if (this.localCompany && this.localCompany.faq_url) {
        return this.localCompany.faq_url;
      }
      return this.faqDefaultUrl;
    },
    baseUrl() {
      return this.shortOrigin + (this.theMarket ? '/' + this.theMarket : '');
    },
  },
  /*mounted() {
    window.addEventListener('resize', this.getDimensions);
    this.getDimensions();
  },
  unmounted() {
    window.removeEventListener('resize', this.getDimensions);
  },*/
  data() {
    return {
      shortOrigin: window.origin || window.location.origin,
      windowOrigin:
        window.origin !== undefined ? `${window.origin}${this.$route?.params?.market ? '/' + this.$route?.params?.market : ''}` : `${window.location.origin}/${this.$route?.params?.market ? '/' + this.$route?.params?.market : ''}`,
      faqDefaultUrl: 'https://intercom-help.eu/enginio/',
      subscribeReady: false,
      isSafari,
      disableGTM: false,
      cookieModalVersion: 'firstCookieModal-v3',
      phoneInputTransl: {},
      supportSubjectOptions: [],
      mobileSmallMaxwidth: 321,
      mobileMaxwidth: 480,
      tabletMinwidth: 481,
      tabletMaxwidth: 990,
      desktopMinwidth: 991,
      desktopSmallWidth: 1023,
      blackboxTimeoutId: null,
      blackboxInterval: 0,
      blackboxIntervalCount: 10,
      /*windowWidth: 0,
      windowHeight: 0,*/
    }
  },
  methods: {
    ...mapMutations({
      setLoadedPageData: 'SET_LOADED_PAGE_DATA',
      setFingerprint: 'SET_FINGERPRINT',
    }),
    ...mapActions({
      setTransl: 'LOAD_TRANSL',
    }),
    /*getDimensions() {
      this.windowWidth = document.documentElement.clientWidth;
      this.windowHeight = document.documentElement.clientHeight;
    },*/
    getId(entity) {
      const objectId = entity.unique_name ? entity.unique_name : entity._id;
      const simpleId = entity;
      return objectId || simpleId;
    },
    navigateBack(fallback) {
      // Vue Router sets history.state.back only when the previous entry is a page in this app
      if (window.history.state?.back) {
        this.$router.go(-1);
        return;
      }

      const startPage = { name: this.$store.getters.authenticated ? 'UserStart' : 'Home' };
      this.$router.push(this.isResolvableRoute(fallback) ? fallback : startPage);
    },
    isResolvableRoute(location) {
      if (!location) {
        return false;
      }
      try {
        return this.$router.resolve(location).matched.length > 0;
      } catch {
        return false;
      }
    },
    setLanguage(language) {
      this.setTransl(language);
      localStorage.setItem('localLanguage', language);
      axios.defaults.headers.common['local-language'] = language;
    },
    // Google analytics events
    pushDataLayer({ parentCompany = {}, company = {}, goodiebag = {}, offer = {}, game = {}, action }) {
      if (!this.disableGTM && window && window.dataLayer) {
        const defaultVal = 'Unknown';
        // const ParentCompany = parentCompany.name ? parentCompany.name : (parentCompany._id ? parentCompany._id : parentCompany);
        const ParentCompany = parentCompany._id ? parentCompany._id : parentCompany;
        const CompanyName = company.name ? company.name : (company._id ? company._id : company);
        const PartnerDashboardId = company.dashboard ? company.dashboard : defaultVal;
        const CollectionName = goodiebag.name ? goodiebag.name : (goodiebag._id ? goodiebag._id : goodiebag);
        const ActivationName = offer.first_header ? offer.first_header : (offer._id ? offer._id : offer);
        const GameName = game.name ? game.name : (game._id ? game._id : game);
        const event = 'Enginio View';
        const EventAction = action;
        const userPhone = this.$store.getters.user?.phone_nr;
        let Phone = defaultVal;
        if (userPhone) {
          Phone = btoa(Array.from(new TextEncoder().encode(userPhone), b => String.fromCharCode(b)).join(''));
        }

        /*if (ParentCompany == defaultVal || /\d/.test(ParentCompany)) {
          console.error('ParentCompany is an id!!!', ParentCompany);
        }*/

        /*console.log('ParentCompany', ParentCompany);
        console.log('CompanyName', CompanyName);
        console.log('PartnerDashboardId', PartnerDashboardId);
        console.log('CollectionName', CollectionName);
        console.log('ActivationName', ActivationName);
        console.log('EventAction', action);*/
        window.dataLayer.push({
          ParentCompany,
          CompanyName,
          PartnerDashboardId,
          CollectionName,
          ActivationName,
          GameName,
          event,
          EventAction,
          Phone
        });
      } else {
        console.log('did not send GTM event');
      }
    },
    gtag(...arg) {
      if (window && window.dataLayer) {
        window.dataLayer.push(arg);
      }
    },
    openUrl(url, newTab) {
      // Safari does not support window.open new tab!
      if (this.isSafari) {
        return location.assign(url); // not a new tab
      }

      if (!newTab || url.includes('enginio.')) {
        location.assign(url); // not a new tab
      } else {
        window.open(url, '_blank'); // new tab
      }
    },
    setPageTitle(companyName, name) {
      if (companyName && name) {
        document.title = `${companyName} | ${name} | Enginio`;
      } else if (companyName || name) {
        document.title = `${companyName || name} | Enginio`;
      } else {
        document.title = 'Enginio';
      }
    },
    findEntityIssue(error) {
      let redirect = false;
      try {
        const { status } = error.response;
        if (status == 401) {
          this.logoutRedirect();
          return;
        } else {
          redirect = true;
        }
      } catch (err) {
        console.log(err);
        redirect = true;
      }
      if (redirect) {
        this.$router.replace({ name: 'UserStart' });
      }
    },
    logoutRedirect() {
      const path = this.$route.path;
      let pushObj = { name: 'Home' };
      let redirect = false;
      if (this.isOnAdminPage) {
        redirect = true;
      } else if (this.isOnPartnerPage) {
        redirect = true;
        pushObj = { name: 'PartnerHome' };
      } else if (this.isOnUserLanding || this.isOnUserPage || this.isOnUserBrandPage) {
        redirect = true;
        if (path.includes('/u-e/')) {
          const companyId = path.split('/u-e/')[1];
          pushObj = { name: 'HomeCompany', params: { companyId } };
        } else if (path.includes('/u-c/') && this.goodiebag) {
          const goodiebagId = path.split('/u-c/')[1];
          pushObj = { name: 'HomeGoodiebag', params: { goodiebagId } };
        } else if (path.includes('/u-a/') && this.offer) {
          const offerId = path.split('/u-a/')[1];
          pushObj = { name: 'HomeOffer', params: { offerId } };
        }
      }

      if (redirect) {
        // this.$router.push(pushObj); // revert to this, if the page refreshes many times
        this.$router.replace(pushObj);
      }
    },
    getCountryCode () {
      return countryCodeForCurrentMarket(window.location);
    },
    isAdmin(user = {}) {
      const roles = user?.roles || [];
      for (let i = 0; i < roles.length; i++) {
        if (roles[i].tenant == 'GLOBAL') {
          return true;
        }
      }
      return false;
    },
    setBlackBox() {
      this.blackboxTimeoutId = setInterval(() => this.useBlackboxString(), 500);
    },
    useBlackboxString() {
      this.blackboxInterval++;
      const igloo = window.IGLOO;
      if (!igloo || typeof igloo.getBlackbox !== 'function') {return;}

      if (this.blackboxInterval >= this.blackboxIntervalCount) {
        console.log('Blackbox timeout');
        clearTimeout(this.blackboxTimeoutId);
      }

      // console.log('igloo:', igloo);
      const bbData = igloo.getBlackbox();
      this.setFingerprint(bbData.blackbox);
      if (bbData.finished) {
        console.log('Blackbox ready', bbData.blackbox);
        // this.setFingerprint(bbData.blackbox);
        clearTimeout(this.blackboxTimeoutId);
      } else {
        // console.log('Blackbox not ready yet');
      }
    },
    // only supports csv and txt, so far
    downloadResFile(response, filetype = 'csv', filename) {
      let _filename;
      if (filename) {
        _filename = filename;
      } else {
        _filename = this.getResFilename(response, `default.${filetype}`);
      }

      const anchor = document.createElement('a');

      if (filetype == 'xlsx') {
        const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = window.URL.createObjectURL(blob);
        anchor.href = url;
        anchor.download = _filename;
        anchor.click();
        window.URL.revokeObjectURL(url);
      } else { // txt or csv
        const mimetype = `text/${filetype === 'csv' ? 'csv' : 'plain'}`;

        // Set the href dynamically based on the mimetype
        anchor.href = `data:${mimetype};charset=utf-8,` + encodeURIComponent(response.data);
        anchor.target = '_blank';
        anchor.download = _filename;
        anchor.click();
      }
    },
    getResFilename(response, fallback) {
      const contentDisposition = response.headers['content-disposition'];
      let filename = fallback;

      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^"]+)"?/);
        if (match && match[1]) {
          filename = match[1];
        }
      }
      return filename;
    },
    getContrastColor(bgColor) {
      // bgColor should be in hex format, e.g. "#ffffff"
      if (!bgColor) return '#000'; // fallback
      const color = bgColor.charAt(0) === '#' ? bgColor.substring(1, 7) : bgColor;
      const r = parseInt(color.substring(0, 2), 16);
      const g = parseInt(color.substring(2, 4), 16);
      const b = parseInt(color.substring(4, 6), 16);
      // Luminance formula
      const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
      return luminance > 0.5 ? '#000' : '#fff';
    },
    setAlert(message, seconds = 4) {
      EventBus.emit('setAlert', {
        message,
        seconds,
        close: 'OK',
        top: 0,
        bot: 'unset',
        left: 0,
        right: 0
      });
    },
    getUnspecial(str = '') {
      if (typeof str !== 'string') {
        return '';
      }
      return str.replace(/[^a-zA-Z ]/g, '');
    }
  }
};
</script>
